// ═══════════════════════════════════════════════════════════
// PA Grants Store — Projects, Organizations, RFRs
// Server-side query pattern with pagination
// ═══════════════════════════════════════════════════════════

import { create } from 'zustand';
import { queryRecords, fetchRecords, createRecord, updateRecord, deleteRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from '../api/fields';
import type { PAProject, PAOrganization, PAReimbursementRequest, PADisaster } from '../types/pagrants';
import type { QueryOptions } from '../api/types';

const FP = FIELDS.paProjects;
const FO = FIELDS.paOrganizations;
const FR = FIELDS.paReimbursementRequests;
const FD = FIELDS.paDisasters;

function mapProject(r: Record<string, unknown>): PAProject {
  return {
    id: r.record_id as number,
    projectNumber: (r[FP.projectNumber] as string) || '',
    title: (r[FP.title] as string) || '',
    organizationId: (r[FP.organizationId] as number) || null,
    disasterId: (r[FP.disasterId] as number) || null,
    workType: (r[FP.workType] as string) || 'pa',
    damageCategory: (r[FP.damageCategory] as string) || '',
    phase: (r[FP.phase] as string) || 'formulation',
    status: (r[FP.status] as string) || 'draft',
    estimatedCost: Number(r[FP.estimatedCost]) || 0,
    obligatedAmount: Number(r[FP.obligatedAmount]) || 0,
    drawdownAmount: Number(r[FP.drawdownAmount]) || 0,
    federalSharePct: Number(r[FP.federalSharePct]) || 75,
    insuranceDeduction: Number(r[FP.insuranceDeduction]) || 0,
    mitigationCost: Number(r[FP.mitigationCost]) || 0,
    siteDescription: (r[FP.siteDescription] as string) || '',
    scopeOfWork: (r[FP.scopeOfWork] as string) || '',
    latitude: (r[FP.latitude] as number) || null,
    longitude: (r[FP.longitude] as number) || null,
    address: (r[FP.address] as string) || '',
    city: (r[FP.city] as string) || '',
    state: (r[FP.state] as string) || '',
    zip: (r[FP.zip] as string) || '',
    completionDeadline: (r[FP.completionDeadline] as string) || '',
    certificationDate: (r[FP.certificationDate] as string) || '',
    repairOrReplace: (r[FP.repairOrReplace] as string) || '',
    notes: (r[FP.notes] as string) || '',
  };
}

function mapOrg(r: Record<string, unknown>): PAOrganization {
  return {
    id: r.record_id as number,
    orgName: (r[FO.orgName] as string) || '',
    orgType: (r[FO.orgType] as string) || '',
    orgRole: (r[FO.orgRole] as string) || '',
    femaId: (r[FO.femaId] as string) || '',
    uei: (r[FO.uei] as string) || '',
    fipsCode: (r[FO.fipsCode] as string) || '',
    primaryEmail: (r[FO.primaryEmail] as string) || '',
    contactEmails: (r[FO.contactEmails] as string) || '',
    phone: (r[FO.phone] as string) || '',
    address: (r[FO.address] as string) || '',
    city: (r[FO.city] as string) || '',
    state: (r[FO.state] as string) || '',
    zip: (r[FO.zip] as string) || '',
    parentOrgId: (r[FO.parentOrgId] as number) || null,
    status: (r[FO.status] as string) || 'active',
    notes: (r[FO.notes] as string) || '',
  };
}

function mapRFR(r: Record<string, unknown>): PAReimbursementRequest {
  return {
    id: r.record_id as number,
    projectId: (r[FR.projectId] as number) || 0,
    organizationId: (r[FR.organizationId] as number) || null,
    requestNumber: (r[FR.requestNumber] as string) || '',
    requestedAmount: Number(r[FR.requestedAmount]) || 0,
    approvedAmount: Number(r[FR.approvedAmount]) || 0,
    federalShare: Number(r[FR.federalShare]) || 0,
    applicantMatch: Number(r[FR.applicantMatch]) || 0,
    status: (r[FR.status] as string) || 'draft',
    costBreakdown: (r[FR.costBreakdown] as string) || '',
    reviewerNotes: (r[FR.reviewerNotes] as string) || '',
    paymentRef: (r[FR.paymentRef] as string) || '',
    submittedBy: (r[FR.submittedBy] as string) || '',
    submittedAt: (r[FR.submittedAt] as string) || '',
    reviewedBy: (r[FR.reviewedBy] as string) || '',
    reviewedAt: (r[FR.reviewedAt] as string) || '',
  };
}

function mapDisaster(r: Record<string, unknown>): PADisaster {
  return {
    id: r.record_id as number,
    disasterNumber: (r[FD.disasterNumber] as string) || '',
    disasterName: (r[FD.disasterName] as string) || '',
    declarationType: (r[FD.declarationType] as string) || '',
    declarationDate: (r[FD.declarationDate] as string) || '',
    incidentId: (r[FD.incidentId] as number) || null,
    incidentPeriodStart: (r[FD.incidentPeriodStart] as string) || '',
    incidentPeriodEnd: (r[FD.incidentPeriodEnd] as string) || '',
    federalSharePct: Number(r[FD.federalSharePct]) || 75,
    status: (r[FD.status] as string) || 'active',
    notes: (r[FD.notes] as string) || '',
  };
}

function projectToPayload(data: Partial<PAProject>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (data.projectNumber !== undefined) p[FP.projectNumber] = data.projectNumber;
  if (data.title !== undefined) p[FP.title] = data.title;
  if (data.organizationId !== undefined) p[FP.organizationId] = data.organizationId;
  if (data.disasterId !== undefined) p[FP.disasterId] = data.disasterId;
  if (data.workType !== undefined) p[FP.workType] = data.workType;
  if (data.damageCategory !== undefined) p[FP.damageCategory] = data.damageCategory;
  if (data.phase !== undefined) p[FP.phase] = data.phase;
  if (data.status !== undefined) p[FP.status] = data.status;
  if (data.estimatedCost !== undefined) p[FP.estimatedCost] = data.estimatedCost;
  if (data.obligatedAmount !== undefined) p[FP.obligatedAmount] = data.obligatedAmount;
  if (data.drawdownAmount !== undefined) p[FP.drawdownAmount] = data.drawdownAmount;
  if (data.federalSharePct !== undefined) p[FP.federalSharePct] = data.federalSharePct;
  if (data.insuranceDeduction !== undefined) p[FP.insuranceDeduction] = data.insuranceDeduction;
  if (data.mitigationCost !== undefined) p[FP.mitigationCost] = data.mitigationCost;
  if (data.siteDescription !== undefined) p[FP.siteDescription] = data.siteDescription;
  if (data.scopeOfWork !== undefined) p[FP.scopeOfWork] = data.scopeOfWork;
  if (data.latitude !== undefined) p[FP.latitude] = data.latitude;
  if (data.longitude !== undefined) p[FP.longitude] = data.longitude;
  if (data.address !== undefined) p[FP.address] = data.address;
  if (data.city !== undefined) p[FP.city] = data.city;
  if (data.state !== undefined) p[FP.state] = data.state;
  if (data.zip !== undefined) p[FP.zip] = data.zip;
  if (data.completionDeadline !== undefined) p[FP.completionDeadline] = data.completionDeadline;
  if (data.certificationDate !== undefined) p[FP.certificationDate] = data.certificationDate;
  if (data.repairOrReplace !== undefined) p[FP.repairOrReplace] = data.repairOrReplace;
  if (data.notes !== undefined) p[FP.notes] = data.notes;
  return p;
}

function orgToPayload(data: Partial<PAOrganization>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (data.orgName !== undefined) p[FO.orgName] = data.orgName;
  if (data.orgType !== undefined) p[FO.orgType] = data.orgType;
  if (data.orgRole !== undefined) p[FO.orgRole] = data.orgRole;
  if (data.femaId !== undefined) p[FO.femaId] = data.femaId;
  if (data.primaryEmail !== undefined) p[FO.primaryEmail] = data.primaryEmail;
  if (data.phone !== undefined) p[FO.phone] = data.phone;
  if (data.address !== undefined) p[FO.address] = data.address;
  if (data.city !== undefined) p[FO.city] = data.city;
  if (data.state !== undefined) p[FO.state] = data.state;
  if (data.status !== undefined) p[FO.status] = data.status;
  if (data.notes !== undefined) p[FO.notes] = data.notes;
  return p;
}

interface PAGrantsState {
  // Projects
  projects: PAProject[];
  projectsTotal: number;
  projectsLoading: boolean;
  projectsPage: number;
  projectsSort: { field: string; dir: 'asc' | 'desc' };
  projectsFilters: Record<string, unknown>;
  fetchProjects: (opts?: Partial<QueryOptions>) => Promise<void>;
  setProjectsPage: (page: number) => void;
  setProjectsSort: (field: string, dir: 'asc' | 'desc') => void;

  // Organizations
  organizations: PAOrganization[];
  orgsLoading: boolean;
  fetchOrganizations: () => Promise<void>;

  // Disasters
  disasters: PADisaster[];
  fetchDisasters: () => Promise<void>;

  // Reimbursements
  reimbursements: PAReimbursementRequest[];
  rfrsTotal: number;
  rfrsLoading: boolean;
  fetchReimbursements: (opts?: Partial<QueryOptions>) => Promise<void>;

  // CRUD
  createProject: (data: Partial<PAProject>) => Promise<PAProject | null>;
  updateProject: (id: number, data: Partial<PAProject>) => Promise<void>;
  deleteProject: (id: number) => Promise<void>;
  createOrganization: (data: Partial<PAOrganization>) => Promise<PAOrganization | null>;
  updateOrganization: (id: number, data: Partial<PAOrganization>) => Promise<void>;

  // V1 compat aliases
  fetchAll: () => Promise<void>;
  loadingProjects: boolean;
  reimbursementRequests: any[];
  damageItems: any[];
  fetchDamageItems: () => Promise<void>;
  createDamageItem: (data: any) => Promise<number>;
  updateDamageItem: (id: any, data: any) => Promise<void>;
  deleteDamageItem: (id: any) => Promise<void>;
  projectCosts: any[];
  fetchProjectCosts: () => Promise<void>;
  createProjectCost: (data: any) => Promise<number>;
  updateProjectCost: (id: any, data: any) => Promise<void>;
  deleteProjectCost: (id: any) => Promise<void>;
  documents: any[];
  fetchDocuments: () => Promise<void>;
  createDocument: (data: any) => Promise<number>;
  deleteDocument: (id: any) => Promise<void>;
  loadingDocs: boolean;
  loadingDamage: boolean;
  laborRecords: any[];
  fetchLabor: () => Promise<void>;
  createLabor: (data: any) => Promise<number>;
  updateLabor: (id: any, data: any) => Promise<void>;
  deleteLabor: (id: any) => Promise<void>;
  equipRecords: any[];
  fetchEquipment: () => Promise<void>;
  createEquip: (data: any) => Promise<number>;
  updateEquip: (id: any, data: any) => Promise<void>;
  deleteEquip: (id: any) => Promise<void>;
  materials: any[];
  fetchMaterials: () => Promise<void>;
  createMaterial: (data: any) => Promise<number>;
  updateMaterial: (id: any, data: any) => Promise<void>;
  deleteMaterial: (id: any) => Promise<void>;
  contracts: any[];
  fetchContracts: () => Promise<void>;
  createContract: (data: any) => Promise<number>;
  updateContract: (id: any, data: any) => Promise<void>;
  deleteContract: (id: any) => Promise<void>;
  rentedEquip: any[];
  fetchRented: () => Promise<void>;
  createRented: (data: any) => Promise<number>;
  updateRented: (id: any, data: any) => Promise<void>;
  deleteRented: (id: any) => Promise<void>;
  donatedResources: any[];
  fetchDonated: () => Promise<void>;
  createDonated: (data: any) => Promise<number>;
  updateDonated: (id: any, data: any) => Promise<void>;
  deleteDonated: (id: any) => Promise<void>;
  insurance: any[];
  fetchInsurance: () => Promise<void>;
  createInsurance: (data: any) => Promise<number>;
  updateInsurance: (id: any, data: any) => Promise<void>;
  deleteInsurance: (id: any) => Promise<void>;
  compliance: any[];
  fetchCompliance: () => Promise<void>;
  createCompliance: (data: any) => Promise<number>;
  updateCompliance: (id: any, data: any) => Promise<void>;
  deleteCompliance: (id: any) => Promise<void>;
  timelineEvents: any[];
  fetchTimeline: () => Promise<void>;
  createTimeline: (data: any) => Promise<number>;
  notes: any[];
  fetchNotes: () => Promise<void>;
  createNote: (data: any) => Promise<number>;
  timeExtensions: any[];
  fetchTimeExtensions: () => Promise<void>;
  createTimeExtension: (data: any) => Promise<number>;
  updateTimeExtension: (id: any, data: any) => Promise<void>;
  createOrg: (data: any) => Promise<number>;
  updateOrg: (id: any, data: any) => Promise<void>;
  deleteOrg: (id: any) => Promise<void>;
  createDisaster: (data: any) => Promise<number>;
  updateDisaster: (id: any, data: any) => Promise<void>;
  deleteDisaster: (id: any) => Promise<void>;
}

export const usePAGrantsStore = create<PAGrantsState>((set, get) => ({
  projects: [], projectsTotal: 0, projectsLoading: false, projectsPage: 1,
  projectsSort: { field: 'created_at', dir: 'desc' },
  projectsFilters: {},

  organizations: [], orgsLoading: false,
  disasters: [],
  reimbursements: [], rfrsTotal: 0, rfrsLoading: false,

  fetchProjects: async (opts) => {
    if (!TABLES.incidents) return; // tables not provisioned
    set({ projectsLoading: true });
    try {
      const { projectsPage, projectsSort } = get();
      const res = await queryRecords<Record<string, unknown>>(TABLES.paProjects, {
        limit: 50,
        offset: (projectsPage - 1) * 50,
        sort: { field: projectsSort.field, direction: projectsSort.dir },
        ...opts,
      });
      set({ projects: res.records.map(mapProject), projectsTotal: res.total });
    } catch (e) { console.error('[PAGrants] fetchProjects', e); }
    set({ projectsLoading: false });
  },

  setProjectsPage: (page) => { set({ projectsPage: page }); get().fetchProjects(); },
  setProjectsSort: (field, dir) => { set({ projectsSort: { field, dir }, projectsPage: 1 }); get().fetchProjects(); },

  fetchOrganizations: async () => {
    set({ orgsLoading: true });
    try {
      // Small table — fetch all
      const res = await fetchRecords<Record<string, unknown>>(TABLES.paOrganizations, { limit: 500 });
      set({ organizations: res.records.map(mapOrg) });
    } catch (e) { console.error('[PAGrants] fetchOrganizations', e); }
    set({ orgsLoading: false });
  },

  fetchDisasters: async () => {
    try {
      const res = await fetchRecords<Record<string, unknown>>(TABLES.paDisasters, { limit: 100 });
      set({ disasters: res.records.map(mapDisaster) });
    } catch (e) { console.error('[PAGrants] fetchDisasters', e); }
  },

  fetchReimbursements: async (opts) => {
    set({ rfrsLoading: true });
    try {
      const res = await queryRecords<Record<string, unknown>>(TABLES.paReimbursementRequests, {
        limit: 50, sort: { field: 'created_at', direction: 'desc' }, ...opts,
      });
      set({ reimbursements: res.records.map(mapRFR), rfrsTotal: res.total });
    } catch (e) { console.error('[PAGrants] fetchReimbursements', e); }
    set({ rfrsLoading: false });
  },

  createProject: async (data) => {
    try {
      const res = await createRecord(TABLES.paProjects, projectToPayload(data));
      await get().fetchProjects();
      return res as unknown as PAProject;
    } catch (e) { console.error('[PAGrants] createProject', e); return null; }
  },

  updateProject: async (id, data) => {
    try {
      await updateRecord(TABLES.paProjects, id, projectToPayload(data));
      await get().fetchProjects();
    } catch (e) { console.error('[PAGrants] updateProject', e); }
  },

  deleteProject: async (id) => {
    try {
      await deleteRecord(TABLES.paProjects, id);
      await get().fetchProjects();
    } catch (e) { console.error('[PAGrants] deleteProject', e); }
  },

  createOrganization: async (data) => {
    try {
      const res = await createRecord(TABLES.paOrganizations, orgToPayload(data));
      await get().fetchOrganizations();
      return res as unknown as PAOrganization;
    } catch (e) { console.error('[PAGrants] createOrganization', e); return null; }
  },

  updateOrganization: async (id, data) => {
    try {
      await updateRecord(TABLES.paOrganizations, id, orgToPayload(data));
      await get().fetchOrganizations();
    } catch (e) { console.error('[PAGrants] updateOrganization', e); }
  },

  // V1 compat aliases
  fetchAll: async () => {
    await Promise.all([
      get().fetchProjects(),
      get().fetchOrganizations(),
      get().fetchDisasters(),
      get().fetchReimbursements(),
    ]);
  },
  loadingProjects: false,
  reimbursementRequests: [] as any[],
  damageItems: [] as any[],
  fetchDamageItems: async () => {},
  createDamageItem: async (_data: any) => 0,
  updateDamageItem: async (_id: any, _data: any) => {},
  deleteDamageItem: async (_id: any) => {},
  projectCosts: [] as any[],
  fetchProjectCosts: async () => {},
  createProjectCost: async (_data: any) => 0,
  updateProjectCost: async (_id: any, _data: any) => {},
  deleteProjectCost: async (_id: any) => {},
  documents: [] as any[],
  fetchDocuments: async () => {},
  createDocument: async (_data: any) => 0,
  deleteDocument: async (_id: any) => {},
  loadingDocs: false,
  loadingDamage: false,
  laborRecords: [] as any[],
  fetchLabor: async () => {},
  createLabor: async (_data: any) => 0,
  updateLabor: async (_id: any, _data: any) => {},
  deleteLabor: async (_id: any) => {},
  equipRecords: [] as any[],
  fetchEquipment: async () => {},
  createEquip: async (_data: any) => 0,
  updateEquip: async (_id: any, _data: any) => {},
  deleteEquip: async (_id: any) => {},
  materials: [] as any[],
  fetchMaterials: async () => {},
  createMaterial: async (_data: any) => 0,
  updateMaterial: async (_id: any, _data: any) => {},
  deleteMaterial: async (_id: any) => {},
  contracts: [] as any[],
  fetchContracts: async () => {},
  createContract: async (_data: any) => 0,
  updateContract: async (_id: any, _data: any) => {},
  deleteContract: async (_id: any) => {},
  rentedEquip: [] as any[],
  fetchRented: async () => {},
  createRented: async (_data: any) => 0,
  updateRented: async (_id: any, _data: any) => {},
  deleteRented: async (_id: any) => {},
  donatedResources: [] as any[],
  fetchDonated: async () => {},
  createDonated: async (_data: any) => 0,
  updateDonated: async (_id: any, _data: any) => {},
  deleteDonated: async (_id: any) => {},
  insurance: [] as any[],
  fetchInsurance: async () => {},
  createInsurance: async (_data: any) => 0,
  updateInsurance: async (_id: any, _data: any) => {},
  deleteInsurance: async (_id: any) => {},
  compliance: [] as any[],
  fetchCompliance: async () => {},
  createCompliance: async (_data: any) => 0,
  updateCompliance: async (_id: any, _data: any) => {},
  deleteCompliance: async (_id: any) => {},
  timelineEvents: [] as any[],
  fetchTimeline: async () => {},
  createTimeline: async (_data: any) => 0,
  notes: [] as any[],
  fetchNotes: async () => {},
  createNote: async (_data: any) => 0,
  timeExtensions: [] as any[],
  fetchTimeExtensions: async () => {},
  createTimeExtension: async (_data: any) => 0,
  updateTimeExtension: async (_id: any, _data: any) => {},
  createOrg: async (data: any) => { const r = await get().createOrganization(data); return (r as any)?.id || 0; },
  updateOrg: async (id: any, data: any) => get().updateOrganization(id, data),
  deleteOrg: async (_id: any) => {},
  createDisaster: async (_data: any) => 0,
  updateDisaster: async (_id: any, _data: any) => {},
  deleteDisaster: async (_id: any) => {},
}));
